import { BadgeType } from '@components'
import { ExpenseClaim, ExpenseClaimExpense, ExpenseClaimStatus } from '@query'
import { formatDate } from '@utils'
import { formatEurosWithOpts } from '@utils/formatCurrency'
import { TFunction } from 'i18next'
import { Link } from 'react-router-dom'
import styled from 'styled-components'

export const expenseClaimExpensesKey = (slug: string, claimId: number) =>
  [slug, 'expense-claim-expenses', claimId] as const

export const expenseDisplayTitle = (merchant: string, t: TFunction) =>
  merchant.trim() ? merchant : t('travelAndExpenses.expense.merchantEmpty')

export const buildExpenseListMeta = (expense: ExpenseClaimExpense, t: TFunction) => {
  const parts: string[] = []
  if (expense.purchase_date) {
    parts.push(formatDate(expense.purchase_date))
  }
  if (expense.category) {
    parts.push(t(`travelAndExpenses.expense.categories.${expense.category}`))
  }
  const vatTotal = (expense.vat_rows ?? []).reduce((sum, row) => sum + row.vat_amount, 0)
  if (vatTotal > 0) {
    parts.push(t('travelAndExpenses.summary.expenseVat', { amount: formatEurosWithOpts(vatTotal) }))
  }
  parts.push(t('travelAndExpenses.summary.expenseAttachment', { count: 1 }))
  return parts.join(' · ')
}

export const claimKey = (slug: string, claimId: number) => [slug, 'expense-claim', claimId]

export const CLAIM_STATUSES: ExpenseClaimStatus[] = [
  'DRAFT',
  'AWAITING_APPROVAL',
  'APPROVED',
  'PAID'
]

export const STATUS_BADGE: Record<ExpenseClaimStatus, BadgeType> = {
  DRAFT: 'default',
  AWAITING_APPROVAL: 'warning',
  APPROVED: 'info',
  PAID: 'success'
}

export const claimantName = (claim: ExpenseClaim) =>
  claim.claimant_profile?.name ||
  [claim.claimant.first_name, claim.claimant.last_name].filter(Boolean).join(' ')

export const PageScroll = styled.div`
  height: 100%;
  overflow: auto;
`

export const PageContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg}rem;
  max-width: ${({ theme }) => theme.layout.pageContentMax}rem;
  min-width: 0;
  padding-bottom: ${({ theme }) => theme.spacing.xl}rem;
`

export const PageTop = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.md}rem;
`

export const PageTopMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  min-width: 0;
`

export const BackLink = styled(Link)`
  display: inline-block;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 600;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.primary};
`

export const PageTitle = styled.h1`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.xl}rem;
  font-weight: 800;
  line-height: 1.25;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

export const PageSubtitle = styled.div`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.iconGray};
`

export const Columns = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.lg}rem;
`

export const MainColumn = styled.div`
  flex: 999 1 ${({ theme }) => theme.layout.mainColumnFlexMin}rem;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg}rem;
`

export const Card = styled.section`
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.lg}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
`

export const Aside = styled(Card).attrs({ as: 'aside' })`
  flex: 1 1 ${({ theme }) => theme.layout.asideFlexMin}rem;
  max-width: ${({ theme }) => theme.layout.asideMax}rem;
  position: sticky;
  top: 0;
  gap: ${({ theme }) => theme.spacing.md}rem;
`

export const CardHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

export const CardTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.lg}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

export const Help = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.iconGray};
`

export const TextLink = styled(Link)`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
`

export const AmountLines = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  row-gap: ${({ theme }) => theme.spacing.xs}rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;

  & > :nth-child(even) {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
`

export const TotalLine = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding-top: ${({ theme }) => theme.spacing.sm}rem;
  margin-top: ${({ theme }) => theme.spacing.xxs}rem;
  border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutralBlack};

  strong {
    font-size: ${({ theme }) => theme.fontSize.lg}rem;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
  }
`

export const FormActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

export const AsideFormActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

export const AsideFormActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-self: stretch;
  width: 100%;
  gap: ${({ theme }) => theme.spacing.sm}rem;

  & > * {
    flex: 0 0 auto;
    min-width: 0;
  }

  & > *:not(:only-child) {
    flex: 1;
    width: 100%;
    justify-content: center;
  }
`

/** Wrap long-label aside actions so text can wrap without shrinking the button. */
export const AsideActionFullWidth = styled.div`
  align-self: stretch;
  width: 100%;

  button {
    width: 100%;
    justify-content: center;
    white-space: normal;
    text-align: center;
  }
`

export const AsideNote = styled(Help)`
  margin-top: ${({ theme }) => theme.spacing.sm}rem;
`

export const AsideFooter = styled(Help)`
  margin-top: ${({ theme }) => theme.spacing.md}rem;
  padding-top: ${({ theme }) => theme.spacing.sm}rem;
  border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};
`
